import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import { Typography } from '@mui/material';
import InputValidationErrorBox from '@ui-parts/InputValidationErrorBox';
import usePreviousValue from '@functional/usePreviousValue';
import { PALETTE, FONT_SIZES } from '~/assets/styles/base';

const Wrapper = styled(Box)`
  margin: 0 auto;
  width: ${({ $width }) => $width};
  position: relative;
`;

const Flex = styled.div`
  // numberの矢印を非表示
  input[type='number']::-webkit-outer-spin-button,
  input[type='number']::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  display: flex;
  justify-content: space-between;
  margin: 12px 0 8px;
`;

const ErrorBoxWrapper = styled.div`
  position: absolute;
  bottom: 0;
  ${({ $isMinError }) =>
    $isMinError
      ? css`
          left: 0;
          transform: translate(-41%, 100%);
        `
      : css`
          right: 0;
          transform: translate(41%, 100%);
        `}
`;

const inputStyle = {
  padding: '8.5px 10px',
  background: PALETTE.white,
  width: '40px',
  fontSize: FONT_SIZES.bodyDefault,
  textAlign: 'center',
  color: PALETTE.text.main,
};

const StyledSlider = styled(Slider)`
  .MuiSlider-track {
    border-width: 7px;
  }
  .MuiSlider-rail {
    border: 8px solid #b2cbcb;
    &:after {
      content: '';
      width: calc(100% + 14px);
      height: 14px;
      background: ${PALETTE.white};
      position: absolute;
      top: -7px;
      left: -7px;
      border-radius: 10px;
    }
  }
  .MuiSlider-thumb {
    &:before {
      background: ${PALETTE.white};
      width: 30px;
      height: 30px;
    }
    &:after {
      content: '';
      position: absolute;
      background: ${PALETTE.primary.main};
      width: 22px;
      height: 22px;
    }
  }
`;

export default function RangeSlider({
  values,
  min,
  max,
  step,
  labels,
  disabled = false,
  width = '492px',
  errors,
  setErrors,
  handleChange,
}) {
  const { t } = useTranslation();

  // NOTE: propsのvaluesと同じ値 input部分は0始まり制御の為、stringとして格納する為に用意
  const [inputValues, setInputValues] = useState(values);
  const prevInputValues = usePreviousValue(inputValues);

  // NOTE: react-form-hookとsliderで互いの変更値の受け渡し制御が難しかった為、ここでバリデート
  // どちらもuseStateでinputValuesのみを監視し、判定対象とする
  // mix maxどちらか１つのみを画面に表示
  const validateInputValue = (area) => {
    const minValue = values[0];
    const maxValue = values[1];
    const res = {
      isMinError: false,
      isMaxError: false,
      message: null,
      area,
    };
    setErrors({ ...res });

    // fromが、下限を下回っている
    if (minValue < min) {
      res.message = t('validation.minNumber', { min });
      res.isMinError = true;
      // toが、下限を下回っている
    } else if (maxValue < min) {
      res.message = t('validation.minNumber', { min });
      res.isMaxError = true;

      // fromが、上限を上回っている
    } else if (minValue > max) {
      res.message = t('validation.maxNumber', { max });
      res.isMinError = true;
      // toが、上限を上回っている
    } else if (maxValue > max) {
      res.message = t('validation.maxNumber', { max });
      res.isMaxError = true;
      // fromがtoより大きい
    } else if (minValue > maxValue) {
      if (area === 'min') {
        res.message = t('validation.maxNumber', { max: maxValue });
        res.isMinError = true;
      } else {
        res.message = t('validation.minNumber', { min: minValue });
        res.isMaxError = true;
      }
      // 最小値を除いてfromがstep刻みに該当しない
    } else if (minValue !== min && minValue % step !== 0) {
      res.message = t('validation.notIncludeStep', { step });
      res.isMinError = true;
      // 最大値を除いてtoがstep刻みに該当しない
    } else if (maxValue !== max && maxValue % step !== 0) {
      res.message = t('validation.notIncludeStep', { step });
      res.isMaxError = true;
    }

    setErrors({ ...res });
  };

  useEffect(() => {
    // NOTE: sliderからの変更でmin、maxかの判別の為、prevの値と比較する
    const targetArea = prevInputValues[0] === inputValues[0] ? 'max' : 'min';
    validateInputValue(targetArea);
  }, [inputValues]);

  // NOTE: inputからの変更 数値だとinput="number"に0始まりの数値が入ってしまうので、Numberしてからストリングに変えてinputに戻す
  const handleInputChange = (newValue, minOrMax) => {
    const newInputValue = [...values];

    newInputValue[minOrMax] = Number(newValue.replace(/[^0-9]+/i, ''));
    setInputValues(newInputValue.map((value) => value.toString()));
    handleChange(newInputValue);
  };

  // NOTE: sliderからの変更
  const handleSliderChange = (newValues) => {
    setInputValues(newValues.map((value) => value.toString()));
    handleChange(newValues);
  };

  return (
    <Wrapper $width={width} data-testid="uiParts-RangeSlider">
      <Flex>
        <Typography variant="bodyDefault" data-testid="lowerLimit-label">
          {labels[0]}
        </Typography>
        <Typography variant="bodyDefault" data-testid="upperLimit-label">
          {labels[1]}
        </Typography>
      </Flex>

      <StyledSlider
        value={values}
        valueLabelDisplay="auto"
        min={min}
        max={max}
        step={step}
        /* istanbul ignore next jestでSliderを変化させるのが難しいため */
        onChange={(e) => {
          handleSliderChange(e.target.value);
        }}
        disableSwap
        disabled={disabled}
      />
      <Flex>
        <TextField
          name="min"
          value={inputValues[0]}
          variant="outlined"
          disabled={disabled}
          error={errors.isMinError && errors.message != null}
          inputProps={{
            'data-testid': 'uiParts-RangeSlider-minInput',
            inputMode: 'numeric',
            pattern: '[0-9]*',
            min,
            max: values[1],
            step,
            style: inputStyle,
          }}
          onChange={(e) => handleInputChange(e.target.value, 0)}
        />
        <TextField
          value={inputValues[1]}
          variant="outlined"
          disabled={disabled}
          error={errors.isMaxError && errors.message != null}
          inputProps={{
            'data-testid': 'uiParts-RangeSlider-maxInput',
            inputMode: 'numeric',
            pattern: '[0-9]*',
            min: values[0],
            max,
            step,
            style: inputStyle,
          }}
          onChange={(e) => handleInputChange(e.target.value, 1)}
        />
      </Flex>
      {errors.message && (
        <ErrorBoxWrapper $isMinError={errors.isMinError}>
          <InputValidationErrorBox
            message={errors.message}
            dataTestId="RangeSlider"
            position="center"
          />
        </ErrorBoxWrapper>
      )}
    </Wrapper>
  );
}

RangeSlider.propTypes = {
  values: PropTypes.arrayOf(PropTypes.number), // NOTE: MUI Sliderはnumber指定
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.node.isRequired,
  labels: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.bool,
  width: PropTypes.string,
  errors: PropTypes.shape({
    isMinError: PropTypes.bool,
    isMaxError: PropTypes.bool,
    message: PropTypes.string,
    area: PropTypes.oneOf(['min', 'max']),
  }).isRequired,
  setErrors: PropTypes.func.isRequired,
  handleChange: PropTypes.func,
};
